import React from 'react';
import { CreditCard, Ticket } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const TIER_LABELS = {
  free_trial: 'Free Trial',
  small: 'Small (≤2,000 words)',
  standard: 'Standard (≤5,000 words)',
  large: 'Large (5,001+ words)',
  song: 'Song Lyrics (£1 / song)',
};

export const PricingModal = ({
  open,
  onOpenChange,
  priceEstimate,
  promoCode,
  setPromoCode,
  applyPromoCode,
  removePromoCode,
  isApplyingPromo,
  isProcessingPayment,
  initiatePayment,
  initiatePayPal,
  onTopUpInstead,
  onProceedFree,
}) => {
  if (!priceEstimate) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-lg border-2 border-border max-w-md" data-testid="pricing-modal" />
      </Dialog>
    );
  }

  const showRushPct = priceEstimate.rush_multiplier > 1
    ? ` (+${Math.round((priceEstimate.rush_multiplier - 1) * 100)}%)`
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg border-2 border-border max-w-md" data-testid="pricing-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-medium tracking-tight font-serif">Pricing Estimate</DialogTitle>
          <DialogDescription className="text-sm text-ash font-sans">Review the pricing for your document</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 border border-border bg-stone">
            <Row label="Word Count" value={priceEstimate.word_count} valueClass="text-lg" />
            <Row label="Tier" value={TIER_LABELS[priceEstimate.tier] || priceEstimate.tier} />
            <Row label="Priority" value={`${priceEstimate.rush_level}${showRushPct}`} valueClass="capitalize" />

            {priceEstimate.free_words_used > 0 && (
              <Row label="Free Words Used" value={priceEstimate.free_words_used} valueClass="text-emerald-400" />
            )}

            {priceEstimate.words_charged > 0 && (
              <>
                <Row label="Words Charged" value={priceEstimate.words_charged} />
                <Row label="Base Price" value={`£${priceEstimate.base_price.toFixed(2)}`} />
                {priceEstimate.rush_surcharge > 0 && (
                  <Row label="Rush Surcharge" value={`+£${priceEstimate.rush_surcharge.toFixed(2)}`} valueClass="text-amber-400" />
                )}
                {priceEstimate.promo_code_applied && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium uppercase tracking-[0.2em] text-emerald-400 flex items-center gap-1">
                      <Ticket size={12} weight="duotone" /> {priceEstimate.promo_code_applied} ({priceEstimate.promo_discount_percent}% off)
                    </span>
                    <span className="text-sm font-sans text-emerald-400">−£{priceEstimate.promo_discount_amount.toFixed(2)}</span>
                  </div>
                )}
              </>
            )}

            <div className="border-t border-border my-3" />
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium uppercase tracking-[0.2em] text-ivory">Total Price</span>
              <span className="text-2xl font-medium font-serif text-gold">£{priceEstimate.total_price.toFixed(2)}</span>
            </div>
          </div>

          {priceEstimate.requires_payment && !priceEstimate.promo_code_applied && (
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Have a code?"
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background focus:border-gold focus:ring-1 focus:ring-gold font-mono text-sm"
                data-testid="promo-code-input"
              />
              <Button
                onClick={applyPromoCode}
                disabled={isApplyingPromo || !promoCode.trim()}
                className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 py-2 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50"
                data-testid="apply-promo-btn"
              >
                {isApplyingPromo ? '...' : 'Apply'}
              </Button>
            </div>
          )}
          {priceEstimate.promo_code_applied && (
            <button
              onClick={removePromoCode}
              className="text-xs text-ash hover:text-ivory font-sans underline"
              data-testid="remove-promo-btn"
            >
              Remove promo code
            </button>
          )}

          {priceEstimate.requires_payment ? (
            <div className="space-y-2">
              <Button
                onClick={() => initiatePayment('proofread')}
                disabled={isProcessingPayment}
                className="w-full rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-6 py-3 transition-colors duration-200 bg-gold text-charcoal hover:bg-gold-dark"
                data-testid="stripe-payment-btn"
              >
                <CreditCard size={16} weight="duotone" className="mr-2" />
                {isProcessingPayment ? 'Processing...' : 'Pay with Stripe'}
              </Button>
              <Button
                onClick={initiatePayPal}
                disabled={isProcessingPayment}
                className="w-full rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-6 py-3 transition-colors duration-200 bg-[#FFC439] text-charcoal hover:bg-[#F0B72A] border border-transparent"
                data-testid="paypal-payment-btn"
              >
                Pay with PayPal
              </Button>
              <p className="text-xs text-center text-ash font-sans">
                Or <button onClick={onTopUpInstead} className="text-gold underline font-medium" data-testid="topup-link">top up free words</button> instead
              </p>
            </div>
          ) : (
            <Button
              onClick={onProceedFree}
              className="w-full rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-6 py-3 transition-colors duration-200 bg-gold text-charcoal hover:bg-gold-dark"
              data-testid="proceed-free-btn"
            >
              Proceed with Free Trial
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, valueClass = '' }) => (
  <div className="flex justify-between items-center mb-2">
    <span className="text-xs font-medium uppercase tracking-[0.2em] text-ash">{label}</span>
    <span className={`text-sm font-medium font-sans ${valueClass}`}>{value}</span>
  </div>
);
